<template>
  <main class="db-connection-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <span class="page-title">数据库连接数监控</span>
        <el-button size="small" plain icon="el-icon-refresh" @click="loadData">刷新</el-button>
        <span class="last-check" v-if="status">上次检查：{{ status.checkedAt }}</span>
      </div>

      <div v-if="status" class="status-grid">
        <div class="stat-card" :class="statusClass">
          <div class="stat-label">当前连接数</div>
          <div class="stat-value">{{ status.current }}</div>
          <div class="stat-bar">
            <div class="stat-bar-fill" :style="{ width: barWidth, background: barColor }"></div>
          </div>
          <div class="stat-meta">
            <span>最大 {{ status.max }}</span>
            <span>阈值 {{ status.threshold }}</span>
          </div>
          <div class="stat-tag" :class="statusClass">{{ statusText }}</div>
        </div>

        <div class="info-card">
          <div class="info-title">基本信息</div>
          <div class="info-row"><span>最大连接数</span><b>{{ status.max }}</b></div>
          <div class="info-row"><span>告警阈值</span><b>{{ status.threshold }}</b></div>
          <div class="info-row"><span>使用率</span><b :class="statusClass">{{ usagePct }}%</b></div>
          <div class="info-row"><span>当前状态</span><b :class="statusClass">{{ statusText }}</b></div>
          <div class="info-row" v-if="status.lastAlarmAt"><span>最近告警</span><b class="alarm-text">{{ status.lastAlarmAt }}</b></div>
          <div class="info-row" v-if="status.lastRecoverAt"><span>最近恢复</span><b class="recover-text">{{ status.lastRecoverAt }}</b></div>
        </div>

        <div class="info-card">
          <div class="info-title">状态明细</div>
          <div v-if="status.byState && Object.keys(status.byState).length">
            <div class="info-row" v-for="(cnt, key) in status.byState" :key="key">
              <span>{{ key === 'null' || !key ? 'idle' : key }}</span>
              <b>{{ cnt }}</b>
            </div>
          </div>
          <div v-else class="empty">暂无数据</div>
        </div>
      </div>

      <div v-if="loading" class="loading-tip">加载中...</div>
      <div v-if="error" class="error-tip">
        <el-alert :title="error" type="error" show-icon :closable="false" />
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: 'DbConnectionMonitor',
  data() {
    return {
      status: null,
      error: '',
      loading: false
    }
  },
  computed: {
    statusClass() {
      if (!this.status) return ''
      if (this.status.status === 'ALARMED') return 'alarmed'
      if (this.status.current >= this.status.threshold) return 'warning'
      if (this.status.current >= this.status.threshold * 0.8) return 'notice'
      return 'normal'
    },
    statusText() {
      if (!this.status) return ''
      if (this.status.status === 'ALARMED') return '告警中'
      if (this.status.current >= this.status.threshold) return '超阈值'
      if (this.status.current >= this.status.threshold * 0.8) return '关注'
      return '正常'
    },
    barWidth() {
      if (!this.status || !this.status.max) return '0%'
      return Math.min(100, (this.status.current / this.status.max) * 100) + '%'
    },
    barColor() {
      if (!this.status) return 'var(--green)'
      if (this.status.status === 'ALARMED') return 'var(--red)'
      if (this.status.current >= this.status.threshold) return 'var(--red)'
      if (this.status.current >= this.status.threshold * 0.8) return 'var(--amber)'
      return 'var(--green)'
    },
    usagePct() {
      if (!this.status || !this.status.max) return 0
      return ((this.status.current / this.status.max) * 100).toFixed(1)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await this.$http.get('/admin/monitoring/db-connection')
        this.status = res
        this.error = ''
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.error) || '加载失败'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.db-connection-page {
  padding: 18px 24px 76px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.page-title {
  font-size: 18px;
  font-weight: 600;
  flex: 1;
  color: var(--text);
}
.last-check {
  font-size: 12px;
  color: var(--muted-2);
}
.status-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}
.stat-card {
  background: var(--panel);
  border-radius: var(--radius);
  padding: 24px;
  border: 1px solid var(--line);
}
.stat-card.alarmed { border-color: var(--red); }
.stat-card.warning { border-color: var(--red); }
.stat-card.notice { border-color: var(--amber); }
.stat-label {
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 8px;
}
.stat-value {
  font-size: 40px;
  font-weight: 700;
  color: var(--text);
  line-height: 1;
}
.stat-bar {
  height: 8px;
  background: var(--panel-3);
  border-radius: 4px;
  margin: 16px 0 8px;
  overflow: hidden;
}
.stat-bar-fill {
  height: 100%;
  transition: width .3s;
  border-radius: 4px;
}
.stat-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--muted-2);
}
.stat-tag {
  display: inline-block;
  margin-top: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(52, 201, 143, 0.15);
  color: var(--green);
}
.stat-tag.alarmed { background: rgba(239, 109, 122, 0.15); color: var(--red); }
.stat-tag.warning { background: rgba(239, 109, 122, 0.15); color: var(--red); }
.stat-tag.notice { background: rgba(244, 183, 79, 0.15); color: var(--amber); }
.info-card {
  background: var(--panel);
  border-radius: var(--radius);
  padding: 20px;
  border: 1px solid var(--line);
}
.info-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}
.info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: var(--muted);
}
.info-row b {
  color: var(--text);
  font-weight: 600;
}
.info-row b.alarmed { color: var(--red); }
.info-row b.warning { color: var(--red); }
.info-row b.notice { color: var(--amber); }
.info-row b.normal { color: var(--green); }
.alarm-text { color: var(--red); }
.recover-text { color: var(--green); }
.empty {
  color: var(--muted-2);
  font-size: 13px;
  text-align: center;
  padding: 24px 0;
}
.loading-tip {
  text-align: center;
  padding: 40px;
  color: var(--muted-2);
  font-size: 13px;
}
.error-tip {
  margin-top: 16px;
}
</style>